import {
  ICustomer,
  WALKIN_DEFAULT_NAME,
  BookingMethods,
  BookingType,
  CustomerTypes,
  IBooking,
  SEARCH_FIELDS
} from "shared-types/index";
import {deburr} from "lodash-es";
import PhoneNumberService from "external-services/phone-number-service"
import GeneralSettingsService from "external-services/general-settings-service/generalSettings.service";

const NS = 'CustomerGeneratorService';

// exported access to static functions (use StatusServiceInstance for functions that rely on angular dependencies)
class CustomerGeneratorService {

  getCustomerInfo(booking: IBooking) {
    const customer: ICustomer = {} as ICustomer;
    if (booking.firstName) {
      customer.firstName = booking.firstName;
    }
    if (booking.lastName) {
      customer.lastName = booking.lastName;
    }
    if (booking.phone) {
      customer.phone = PhoneNumberService.formatNumber(customer.phone, '', false);
    }
    if (booking.email) {
      customer.email = booking.email;
    }
    return customer;
  }

  /**
   * Does 2 string manipulations:
   * - Returns only a-z and A-Z characters for searching
   * - Also sanitises the field on the `vm.customer[field]` model to allow for Latin Extended characters, plus spaces and hyphens
   * Note that characters with diacritics, such as "é", "ü", etc, will be converted to basic latin for the return value,
   * to help searching api quicker, but the field still allows these characters to be entered and saved. They won't be searchable
   * though (see https://nowbookit.atlassian.net/browse/NBI-3618).
   * @param query - accepts any string
   * @param field - must be SEARCH_FIELDS.FIRST_NAME or SEARCH_FIELDS.LAST_NAME
   * @param customerObject - in AngularJS, this is the model's parent object
   * @returns {string} - returns only a-z and A-Z characters for searching
   */
  restrictNameFields(query: string, field?: SEARCH_FIELDS, customerObject?: any): string {
    const originalName = query;

    /**
     * normalise accented characters for search, but still allow them to be entered and saved, they
     * just won't show up in searches
     */
    query = deburr(query);

    if (field && customerObject) {
      // strip out spaces, numbers and other characters, but still leave latin extended characters, spaces and hyphens
      const RE1 = /[^a-z\u00C0-\u02AB -]/gi;
      customerObject[field] = originalName.replace(RE1, '');
    }

    const RE2 = /[^a-z]/gi; // only latin characters for search (not latin extended)
    // strip out spaces, numbers and other characters
    query = query.replace(RE2, '');

    return query;
  }

  /**
   * Basic validation before searching for an email address
   */
  minimumEmail(query: string): boolean {
    if (query.length < 5) {
      return false;
    }

    // @ must not be at the beginning or end, but must exist
    const atIndex = query.indexOf('@');
    if (atIndex === 0 || atIndex === query.length-1 || atIndex === -1) {
      return false;
    }

    return true;
  }

  /**
   * Checks if the query starts with '(', '+' or a number and then returns true if numbers are found.
   * Otherwise it will assume it is not a phone number.
   * @param query - query to check
   * @return - if it is a phone number or not
   */
  isPhoneNumber(query: string): boolean {
    if (query) {
      const hasNumbers = () => query.replace(/[^0-9]/g, '') !== '';
      const firstCharIsNumber = () => query[0].replace(/[^0-9]/g, '') !== '';

      if (query[0] === '(' || query[0] === '+' || firstCharIsNumber()) {
        return hasNumbers();
      }
    }
    return false;
  }

  isPhoneNumberTooShortForSearch(query: string): boolean {
    if (!query) {
      return true;
    }
    const numbers = query.replace(/[^0-9]/g, '');
    return numbers.length < 6;
  }


  /**
     * Return a single line value represents the customer main informations
     * @param {Object} customer
     * @param {boolean} showPhone
     * @returns {String} possible formats are:
     * [firstName][ lastName][, company][, phone]
     * [company][, [firstName][ lastName][, phone]
     */

  public getDisplayName(customer: ICustomer | IBooking, showPhone: boolean = null): string {
    const nameParts: string[] = [];
    if (customer?.firstName && customer.firstName.trim().length > 0) {
      nameParts.push(customer.firstName);
    }
    if (customer?.lastName && customer.lastName.trim().length > 0) {
      nameParts.push(customer.lastName);
    }

    const displayParts = [nameParts.join(' ').trim()];
    if (customer?.company && customer.company.trim().length > 0) {
      const settings = GeneralSettingsService.getSettingsCache();
      if (settings && settings.companyNameFirst) {
        displayParts.unshift(customer.company);
      } else {
        displayParts.push(customer.company);
      }
    }
    if (showPhone && customer?.phone && customer.phone.trim().length > 0) {
      const phone = PhoneNumberService.formatNumber(customer.phone, '', false);
      if (phone) {
        displayParts.push(phone);
      }
    }

    return displayParts.join(', ');
  }

  public getEmptyCustomer(bookingMethod: BookingMethods, bookingType: BookingType = null, subscribed:boolean = true): {
    customerType?: CustomerTypes;
    firstName?: string;
    subscribed: boolean;
    tags: any[];
  } {

    if (bookingMethod === BookingMethods.walkin) {
      return {
        customerType: CustomerTypes.inline,
        firstName: WALKIN_DEFAULT_NAME,
        subscribed: true,
        tags: []
      };
    }

    if (bookingType === BookingType.StandbyList) {
      return {
        customerType: CustomerTypes.inline,
        firstName: '',
        subscribed: true,
        tags: []
      };
    }

    return {
      subscribed: subscribed,
      tags: []
    };
  }

  public setEmptyCustomer(existingCustomer: ICustomer, bookingMethod: BookingMethods, bookingType: BookingType): any {
    const emptyCustomer = this.getEmptyCustomer(bookingMethod, bookingType);
    if (existingCustomer) {
      // this logic affects standby, but not walkin, as walkins should always have 'inline' form by default
      if (bookingMethod !== BookingMethods.walkin) {
        if (bookingType === BookingType.Booking) {
          // if the old existingCustomer was for standby, but it was removed, we set it back to full customer form
          existingCustomer.customerType = CustomerTypes.existing;
        } else if (bookingType === BookingType.StandbyList) {
          // resets the first name when adding standby if customer form already visited, but name not filled in
          if (!existingCustomer.firstName) {
            existingCustomer.firstName = '';
            existingCustomer.customerType = CustomerTypes.inline;
          }
        }
      }
      return {
        ...emptyCustomer,
        ...existingCustomer
      }
    } else {
      return emptyCustomer;
    }
  }
}


// creates a singleton instance to export
const instance = new CustomerGeneratorService();
export {instance as CustomerGeneratorService};
